import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import { clearAuthKitState, hardRedirectToLogin } from "@/shared/auth/authRecovery";
import { consumePostLoginRedirect } from "@/shared/auth/postLoginRedirect";

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

export function InitiateLogin() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isLoading, signIn } = useAuth();
  const hasStarted = useRef(false);

  useEffect(() => {
    if (isLoading || hasStarted.current) return;
    hasStarted.current = true;

    if (user) {
      navigate(consumePostLoginRedirect() ?? "/", { replace: true });
      return;
    }

    // Strip any stale AuthKit state so the fresh signIn() owns the code_verifier
    // that WorkOS will validate against on the /callback round-trip.
    clearAuthKitState();

    (async () => {
      try {
        await signIn();
      } catch (err) {
        console.error("[InitiateLogin] signIn failed", err);
        hardRedirectToLogin();
      }
    })();
  }, [isLoading, user, signIn, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 3,
        background: theme.palette.background.default,
      }}
    >
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            border: `3px solid ${theme.palette.divider}`,
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        />
        <CircularProgress size={48} sx={{ color: "primary.main", position: "absolute" }} />
      </Box>
      <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
        Redirecting to sign in...
      </Typography>
    </Box>
  );
}
